import { useMemo, useState } from "react";
import { CheckCircle2, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getSession } from "@/lib/auth";
import { AREAS, buildMessage, EMAIL, OWNER, PHONE_DIGITS, PHONE_DISPLAY, ROOMS } from "@/lib/constants";
import { useLang } from "@/lib/i18n";

const AREA_BY_SLUG = {
  "igi-airport": "IGI Airport",
  yashobhoomi: "Yashobhoomi Convention Centre",
  "kapashera-border": "Kapashera Border",
};

const INITIAL = {
  name: "", mobile: "", checkIn: "", checkOut: "", guests: "1", room: ROOMS[0].name,
  area: "", breakfast: "No", transfer: "No", vehicle: "Sedan", transferType: "Pickup", special: "", consent: false,
};

export const EnquiryForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState(() => {
    const slug = new URLSearchParams(window.location.search).get("area");
    const session = getSession();
    const guest = session?.user?.role === "guest" ? session.user : null;
    return {
      ...INITIAL,
      area: AREA_BY_SLUG[slug] || "",
      name: guest?.name || "",
      mobile: guest?.phone || "",
    };
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const message = useMemo(() => buildMessage(form), [form]);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const saveInquiry = async () => {
    try {
      await api.post("/inquiries", {
        guest_name: form.name, mobile: form.mobile, check_in: form.checkIn, check_out: form.checkOut,
        guests: form.guests, room: form.room, area: form.area, breakfast: form.breakfast,
        transfer: form.transfer, vehicle: form.vehicle, transfer_type: form.transferType, special: form.special,
      });
    } catch {
      toast.error("Your enquiry could not be saved to the inbox, but WhatsApp still opened.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.consent || sending) return;
    setSending(true);
    await saveInquiry();
    setSending(false);
    setSubmitted(true);
    window.open(`https://wa.me/${PHONE_DIGITS}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  const emailDraft = async () => {
    if (form.name && form.mobile) await saveInquiry();
    window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent("Hotel room enquiry")}&body=${encodeURIComponent(message)}`;
  };

  return (
    <section className="enquiry-wrap" id="enquiry">
      <div className="enquiry-intro">
        <div className="eyebrow">{t.enquiry.eyebrow}</div>
        <h2>{t.enquiry.title}</h2>
        <p>{t.enquiry.intro}</p>
        <div className="contact-mini">
          <div className="avatar">SK</div>
          <div>
            <strong>{OWNER}</strong>
            <span>{t.enquiry.role}</span>
            <a href={`tel:${PHONE_DIGITS}`} data-testid="enquiry-call-link">
              {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>
      <form className="booking-form" onSubmit={handleSubmit} data-testid="booking-enquiry-form">
        <div className="form-grid">
          <label>
            {t.enquiry.name}
            <input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder={t.enquiry.namePh} data-testid="guest-name-input" />
          </label>
          <label>
            {t.enquiry.mobile}
            <input required type="tel" value={form.mobile} onChange={(e) => update("mobile", e.target.value)} placeholder={t.enquiry.mobilePh} data-testid="guest-mobile-input" />
          </label>
          <label>
            {t.enquiry.checkIn}
            <input type="date" value={form.checkIn} onChange={(e) => update("checkIn", e.target.value)} data-testid="check-in-date-input" />
          </label>
          <label>
            {t.enquiry.checkOut}
            <input value={form.checkOut} onChange={(e) => update("checkOut", e.target.value)} placeholder={t.enquiry.checkOutPh} data-testid="check-out-input" />
          </label>
          <label>
            {t.enquiry.guests}
            <select value={form.guests} onChange={(e) => update("guests", e.target.value)} data-testid="guest-count-select">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </label>
          <label>
            {t.enquiry.room}
            <select value={form.room} onChange={(e) => update("room", e.target.value)} data-testid="room-package-select">
              {ROOMS.map((room) => (
                <option key={room.name}>{room.name}</option>
              ))}
            </select>
          </label>
          <label>
            {t.enquiry.area}
            <select value={form.area} onChange={(e) => update("area", e.target.value)} data-testid="area-preference-select">
              <option value="">{t.enquiry.areaPh}</option>
              {AREAS.map((area) => (
                <option key={area}>{area}</option>
              ))}
            </select>
          </label>
          <label>
            {t.enquiry.breakfast}
            <select value={form.breakfast} onChange={(e) => update("breakfast", e.target.value)} data-testid="breakfast-select">
              <option value="No">{t.enquiry.no}</option>
              <option value="Yes">{t.enquiry.yes}</option>
            </select>
          </label>
          <label>
            {t.enquiry.transfer}
            <select value={form.transfer} onChange={(e) => update("transfer", e.target.value)} data-testid="transfer-required-select">
              <option value="No">{t.enquiry.no}</option>
              <option value="Yes">{t.enquiry.yes}</option>
            </select>
          </label>
          <label>
            {t.enquiry.vehicle}
            <select value={form.vehicle} onChange={(e) => update("vehicle", e.target.value)} data-testid="vehicle-select">
              <option>Sedan</option>
              <option>XL taxi</option>
            </select>
          </label>
          <label>
            {t.enquiry.transferType}
            <select value={form.transferType} onChange={(e) => update("transferType", e.target.value)} data-testid="transfer-type-select">
              <option value="Pickup">{t.enquiry.pickupOpt}</option>
              <option value="Drop">{t.enquiry.dropOpt}</option>
            </select>
          </label>
          <label className="wide">
            {t.enquiry.special}
            <textarea value={form.special} onChange={(e) => update("special", e.target.value)} placeholder={t.enquiry.specialPh} data-testid="special-request-input" />
          </label>
        </div>
        <label className="consent">
          <input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} data-testid="consent-checkbox" />
          <span>{t.enquiry.consent}</span>
        </label>
        <div className="form-actions">
          <button className="button button-gold" type="submit" disabled={sending} data-testid="submit-whatsapp-button">
            <MessageCircle size={17} /> {sending ? t.enquiry.sending : t.enquiry.submit}
          </button>
          <button className="button button-outline" type="button" onClick={emailDraft} data-testid="email-draft-button">
            <Mail size={17} /> {t.enquiry.email}
          </button>
        </div>
        {submitted && (
          <div className="success-message" data-testid="enquiry-success-message">
            <CheckCircle2 size={17} /> {t.enquiry.success}
          </div>
        )}
      </form>
    </section>
  );
};
